import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Trash2, Copy,
  ChevronUp, ChevronDown, RotateCw, Bold, Italic, Underline,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Wand2,
} from 'lucide-react';
import type { Font, PrintableColor } from '@customedge/shared';
import { nearestPantone } from '@customedge/shared';
import { useDesignStore } from '../../store/designStore';
import { useColorBudget, findOffendingObject, selectOffendingObject } from '../../hooks/useColorBudget';
import { Slider } from '../Slider';
import { ImageCorrectionModal } from '../tools/ImageCorrectionModal';
import { ColorPicker } from '../tools/ColorPicker';
import { ToolSection } from '../tools/shared/ToolSection';
import { PropertyRow, propertyInputStyle, PANEL_FIELD_CLASS } from '../tools/shared/PropertyRow';
import { segmentedGroup, segmentBtn, iconSegmentBtn } from '../tools/shared/SegmentedControl';
import { FontCombobox } from '../tools/shared/FontCombobox';
import { getLogicalSize } from '../../lib/canvasObjectHelpers';
import { ensureFontLoaded } from '../../lib/fontLoader';

async function fetchFonts(): Promise<Font[]> {
  const r = await fetch('/api/fonts');
  const d = await r.json();
  return (d.fonts ?? []) as Font[];
}

// Same fetch ColorsUsedPanel.tsx already uses for its own PMS readout — kept
// as a local duplicate rather than a shared import since neither file
// exports it and this codebase's existing precedent (HISTORY_SNAPSHOT_PROPS
// vs. designDownload.ts's CANVAS_JSON_PROPS) already tolerates a small,
// intentional duplicate over a cross-module import for something this thin.
async function fetchPrintableColors(): Promise<PrintableColor[]> {
  const r = await fetch('/api/printable-colors');
  const d = await r.json();
  return (d.colors ?? []) as PrintableColor[];
}

// Persists which admin PrintableColor a fill pick resolved to, onto the
// object itself (spotColorId — see historyStore.ts's HISTORY_SNAPSHOT_PROPS
// and vectorPdfService.ts's buildSpotElements(), the print-worker's own
// reader of this same field, called from printWorker.ts). Scoped to
// text/textbox only — matches buildSpotElements()'s own scope (curved-text
// has no SVG override at all yet; shapes/paths would need Fabric's own path
// serialization duplicated there, a separate follow-up). null (not omitted)
// when no PMS match is close enough, so a stale tag from a PREVIOUS color
// pick doesn't survive onto a new color that no longer resolves to any spot
// color. A top-level exported pure function (not an inline closure) so it
// can be unit tested directly — matches this codebase's own established
// convention for panel-adjacent logic (see ColorsUsedPanel.tsx's
// computeAutoFillColors + its own __tests__/ColorsUsedPanel.test.ts).
export function withSpotColorTag(
  objType: string | undefined,
  hex: string,
  props: Record<string, any>,
  printableColors: PrintableColor[],
): Record<string, any> {
  if (objType !== 'textbox' && objType !== 'text') return props;
  const match = nearestPantone(hex, printableColors);
  return { ...props, spotColorId: match?.id ?? null };
}

import type { FabricCanvas } from '../../types/fabric';

type TextTypeKind = 'text' | 'curved' | 'vertical';

function kindOf(obj: any): TextTypeKind {
  if (obj.type === 'curved-text' || obj.type === 'curvedText') return 'curved';
  if (obj.isVerticalText) return 'vertical';
  return 'text';
}

interface PropertyPanelProps {
  canvas: FabricCanvas | null;
}

export function PropertyPanel({ canvas }: PropertyPanelProps) {
  const { t } = useTranslation();
  const { selectedObject } = useDesignStore();
  const activeSide = useDesignStore(state => state.sides.find(s => s.sideKey === state.activeSideKey));
  // Two separate budgets, each excluding only ITS OWN control's current
  // value — an object can have both a fill and a stroke, each occupying its
  // own distinct budget slot; editing one must still count the OTHER as
  // "used" (see useColorBudget's own param comment for why a single shared
  // exclusion would be wrong here).
  const fillColorBudget   = useColorBudget(activeSide, (selectedObject as any)?.fill);
  const strokeColorBudget = useColorBudget(activeSide, (selectedObject as any)?.stroke);
  const [, forceUpdate] = useState(0);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  // Same font list/loader TextTool.tsx (Add) uses — Edit needs it too so an
  // already-placed text object's font can be changed, not just chosen at
  // creation time. Fetched unconditionally (cheap, cached via staleTime) so
  // the hook order stays stable regardless of what's selected.
  const { data: fonts = [] } = useQuery({ queryKey: ['fonts'], queryFn: fetchFonts, staleTime: 300_000 });
  const { data: printableColors = [] } = useQuery({ queryKey: ['printable-colors'], queryFn: fetchPrintableColors, staleTime: 300_000 });

  useEffect(() => {
    if (!canvas) return;
    const refresh = () => forceUpdate(n => n + 1);
    canvas.on('object:modified', refresh);
    canvas.on('selection:created', refresh);
    canvas.on('selection:updated', refresh);
    return () => {
      canvas.off('object:modified', refresh);
      canvas.off('selection:created', refresh);
      canvas.off('selection:updated', refresh);
    };
  }, [canvas]);

  if (!selectedObject) return (
    <div style={{ padding: 14, height: '100%', overflowY: 'auto' }}>
      <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('properties.selectObject')}</p>
    </div>
  );

  const obj = selectedObject as any;
  const isText = ['text', 'textbox', 'curved-text', 'curvedText'].includes(obj.type ?? '');
  const isCurved = obj.type === 'curved-text' || obj.type === 'curvedText';
  const isImage = obj.type === 'image';
  // Same rule useColorBudget's collectObjectColors already enforces when
  // counting OTHER objects' strokes — a stroke with width 0 renders nothing,
  // so it shouldn't be treated as a real, budget-relevant color for THIS
  // object either (see the strokeColor Row below).
  const hasVisibleStroke = typeof obj.strokeWidth === 'number' && obj.strokeWidth > 0;

  const set = (props: Record<string, any>) => {
    obj.set(props);
    obj.setCoords?.();
    canvas?.renderAll();
    // obj.set() doesn't fire object:modified the way a real canvas drag does, so without
    // this, every field below (fontSize, color, weight, ...) genuinely applies to the
    // canvas but the displayed input snaps back to its old value on next render — making
    // the change look like it silently failed.
    forceUpdate(n => n + 1);
    // LayerPanel.tsx rebuilds its list only on real Fabric events
    // (object:modified/added/removed/...) — without firing this too, editing
    // a text object's content here (or any other property) leaves its layer
    // row showing stale info (e.g. the old text as its name) until some
    // unrelated canvas interaction happens to trigger a rebuild.
    canvas?.fire('object:modified', { target: obj });
  };

  // Blocks a fill/stroke pick that would exceed the budget, then immediately
  // jumps to (selects) whichever OTHER object on this side is holding up the
  // limit — per direct user request ("force the user to limit the color in
  // current object or navigate them to correct the color in existing
  // object"): rather than just showing a blocked-message and leaving the
  // customer to hunt for the culprit themselves, this selects it for them so
  // PropertyPanel immediately re-renders showing THAT object's own color
  // control, ready to edit right there. `budget` is whichever of
  // fillColorBudget/strokeColorBudget the calling input is gated by — the
  // exclusion (this object's own OTHER property) already lives in that
  // budget, so `findOffendingObject`'s own `excludeObject: obj` only needs
  // to skip re-selecting the object the customer is already looking at.
  const applyColorIfAllowed = (budget: ReturnType<typeof useColorBudget>, hex: string, props: Record<string, any>) => {
    if (budget.isColorAllowed(hex)) { set(withSpotColorTag(obj.type, hex, props, printableColors)); return; }
    if (!activeSide || !canvas) return;
    const offending = findOffendingObject(canvas, activeSide, obj);
    if (offending) selectOffendingObject(canvas, offending);
  };

  const remove = () => {
    canvas?.remove(selectedObject);
    canvas?.renderAll();
  };

  const duplicate = () => {
    if (!canvas) return;
    obj.clone((cloned: any) => {
      cloned.set({ left: (obj.left ?? 0) + 16, top: (obj.top ?? 0) + 16 });
      canvas.add(cloned);
      canvas.setActiveObject(cloned);
      canvas.renderAll();
    });
  };

  // const bringForward = () => { canvas?.bringForward(obj); canvas?.renderAll(); forceUpdate(n => n + 1); };
  // const sendBackward = () => { canvas?.sendBackwards(obj); canvas?.renderAll(); forceUpdate(n => n + 1); };
  const moveSelectedLayer = (direction: 1 | -1) => {
  if (!canvas) return;

  const objects = canvas.getObjects();
  const currentIndex = objects.indexOf(obj);
  const nextIndex = currentIndex + direction;

  if (
    currentIndex < 0 ||
    nextIndex < 0 ||
    nextIndex >= objects.length
  ) return;

  // Keep template backgrounds below design objects.
  if ((objects[nextIndex] as any)?.isTemplateBackground) return;

  canvas.moveTo(obj, nextIndex);
  canvas.renderAll();
  canvas.fire('object:modified', { target: obj });

  forceUpdate(n => n + 1);
};

const bringForward = () => moveSelectedLayer(1);
const sendBackward = () => moveSelectedLayer(-1);

  // Converting Straight <-> Curved <-> Vertical after placement (per direct
  // user request — Add lets you pick this up front, Edit didn't let you
  // change your mind afterward). Fabric has no in-place type mutation for
  // this ('curved-text' is a distinct fabric.Object subclass with its own
  // render path, and 'vertical' is really just a Textbox whose text content
  // is pre-split with newlines) — so this rebuilds a fresh object of the
  // target kind carrying over every shared property (text content minus any
  // vertical stacking, font, size, color, style, stroke, opacity, position,
  // rotation) and swaps it in for the current selection, exactly mirroring
  // what TextTool.tsx's addText() constructs for each kind at creation time.
  const convertTextType = async (target: TextTypeKind) => {
    if (!canvas || kindOf(obj) === target) return;
    const { fabric } = await import('fabric');
    const { width: cw, height: ch } = getLogicalSize(canvas);
    // Un-stack vertical text back to its real single-line content before
    // handing it to another kind's constructor.
    const rawText: string = obj.isVerticalText ? String(obj.text ?? '').replace(/\n/g, '') : String(obj.text ?? '');
    const shared = {
      fontFamily: obj.fontFamily ?? 'Poppins',
      fill: obj.fill ?? '#000000',
      fontWeight: obj.fontWeight === 'bold' ? 'bold' : 'normal',
      fontStyle: obj.fontStyle === 'italic' ? 'italic' : 'normal',
      opacity: obj.opacity ?? 1,
      left: obj.left, top: obj.top, angle: obj.angle ?? 0,
    };
    const strokeWidth = obj.strokeWidth ?? 0;
    const strokeColor = obj.stroke ?? '#000000';
    const fontSize = obj.fontSize ?? 20;

    let next: any;
    if (target === 'curved') {
      if (!(fabric as any).CurvedText) return;
      const fittedDiameter = Math.min(obj.diameter ?? 250, Math.min(cw, ch) * 0.85);
      const radius = fittedDiameter / 2;
      const fittedFontSize = Math.max(1, Math.min(fontSize, Math.floor(radius / 2.8)));
      next = new (fabric as any).CurvedText(rawText, {
        ...shared, diameter: fittedDiameter, fontSize: fittedFontSize,
        strokeStyle: strokeWidth > 0 ? strokeColor : null, strokeWidth,
      });
    } else if (target === 'vertical') {
      const stacked = rawText.split('').join('\n');
      next = new fabric.Textbox(stacked, {
        ...shared, width: Math.min(fontSize * 1.6, cw * 0.85), fontSize,
        textAlign: 'center', lineHeight: 1,
        underline: obj.underline === true, charSpacing: obj.charSpacing ?? 0,
        stroke: strokeWidth > 0 ? strokeColor : undefined, strokeWidth,
      });
      (next as any).isVerticalText = true;
    } else {
      // `obj.width` is only a meaningful "text box width" when the source is
      // already a real Textbox — a CurvedText's own `width` is auto-derived
      // from its circular-render canvas (see fabric.curvedText.ts's
      // `_render`), not something the customer ever sized intentionally.
      // Carrying that over verbatim when converting curved -> straight
      // produced a Textbox far narrower than the placeholder-fitted default,
      // forcing the same text into many cramped wrapped lines instead of the
      // normal single/few-line layout. Only trust `obj.width` coming from
      // another straight Textbox (vertical already forces its own width).
      const sourceHasRealWidth = kindOf(obj) === 'text';
      next = new fabric.Textbox(rawText, {
        ...shared, width: sourceHasRealWidth ? (obj.width ?? cw * 0.85) : cw * 0.85, fontSize,
        textAlign: (sourceHasRealWidth && obj.textAlign) || 'center',
        underline: obj.underline === true, charSpacing: obj.charSpacing ?? 0,
        stroke: strokeWidth > 0 ? strokeColor : undefined, strokeWidth,
      });
    }

    canvas.remove(obj);
    canvas.add(next);
    // Position is carried over via `shared.left`/`shared.top` in the
    // constructor above (kept at the original spot) rather than re-centered —
    // converting a type shouldn't relocate text the customer already placed.
    next.setCoords?.();
    canvas.setActiveObject(next);
    canvas.renderAll();
    useDesignStore.getState().setSelectedObject(next);
  };

  // Fabric's rotation transform only ever cares about sin/cos of the angle,
  // so it renders identically whether `angle` is stored as e.g. 270, -90, or
  // -450 — Fabric itself does NOT normalize this value on write. That means
  // nudging can just add `delta` straight onto whatever's currently
  // displayed and hand Fabric the raw (possibly negative, possibly >360)
  // result directly, with no modulo at all — this is what actually fixes
  // the reported bug: clicking "-90°" twice used to land on the raw
  // Math.round(((angle - 90) % 360 + 360) % 360) value of 180 (no sign),
  // which reads as "did this just add 90 instead of subtract?" rather than
  // "yes, this is a clean 180° turn." Reducing ONLY happens for display, and
  // only for angles read from elsewhere (e.g. a freshly loaded saved
  // design) — genuinely round-tripping through nudgeAngle never re-derives
  // the sign from a reduced value, so a run of "-90°" clicks keeps
  // decreasing (90, 0, -90, -180, -270, ...) exactly like the user clicked,
  // instead of a boundary tie-break silently flipping the sign at ±180.
  const rawAngle = (obj.angle ?? 0) % 360;
  const angle = Math.round(rawAngle > 180 ? rawAngle - 360 : rawAngle < -180 ? rawAngle + 360 : rawAngle);
  const nudgeAngle = (delta: number) => set({ angle: angle + delta });

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header — object type + quick actions, everything one click away.
          Stays pinned (flexShrink: 0) while the properties below it scroll,
          so the delete/duplicate/reorder actions are never scrolled out of
          reach in this section's fixed-height slot. */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 12px', background: 'var(--bg-subtle, #f8fafc)',
        borderBottom: '1px solid var(--border)', flexShrink: 0,
      }}>
        <strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted)' }}>
          {capitalize(obj.type ?? 'Object')}
        </strong>
        <div style={{ display: 'flex', gap: 2 }}>
          <IconBtn title={t('properties.duplicate')} onClick={duplicate}><Copy size={14} strokeWidth={1.75} /></IconBtn>
          <IconBtn title={t('properties.bringForward')} onClick={bringForward}><ChevronUp size={14} strokeWidth={1.75} /></IconBtn>
          <IconBtn title={t('properties.sendBackward')} onClick={sendBackward}><ChevronDown size={14} strokeWidth={1.75} /></IconBtn>
          <IconBtn title={t('toolbar.delete')} onClick={remove} danger><Trash2 size={14} strokeWidth={1.75} /></IconBtn>
        </div>
      </div>

      <div style={{ padding: 12, fontSize: 13, display: 'flex', flexDirection: 'column', overflowY: 'auto', flex: 1 }}>
        {/* Image — thumbnail + re-entry into the step-by-step correction
            wizard (Convert to Vector / Remove Background or Color / Select
            Colors / Crop & Rotate), for a photo that's already placed on the
            canvas. Per direct user request, this sits ABOVE Transform. The
            wizard always restages from the object's own pristine
            originalUploadUrl (never today's already-edited canvas image —
            see ImageCorrectionModal.tsx) and only touches this object at all
            if the user reaches its final "Update Image" action; closing the
            modal any earlier leaves the object exactly as it was. */}
        {isImage && (
          <ToolSection title={t('properties.image')} divider={false}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div style={{
                width: 48, height: 48, borderRadius: 6, flexShrink: 0, overflow: 'hidden',
                border: '1px solid var(--border)',
                background: 'repeating-conic-gradient(#e2e8f0 0% 25%, #ffffff 0% 50%) 0 0 / 10px 10px',
              }}>
                <img src={obj.getSrc?.() ?? obj.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>
              <button
                onClick={() => setCorrectionOpen(true)}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  fontSize: 12, fontWeight: 600, padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                  border: '1px solid var(--border)', background: 'white', color: 'var(--text)',
                }}
              >
                <Wand2 size={14} strokeWidth={1.75} />
                {t('properties.correctColors')}
              </button>
            </div>
          </ToolSection>
        )}

        {isText && (
          <TextEditSections
            obj={obj} set={set} canvas={canvas} isCurved={isCurved}
            fonts={fonts}
            fillColorBudget={fillColorBudget} applyColorIfAllowed={applyColorIfAllowed}
            convertTextType={convertTextType}
          />
        )}

        <ToolSection title={t('properties.appearance')}>
          <PropertyRow label={t('properties.opacity')}>
            <Slider min={0} max={1} step={0.05} value={obj.opacity ?? 1}
              onChange={v => set({ opacity: v })}
              valueLabel={`${Math.round((obj.opacity ?? 1) * 100)}%`} />
          </PropertyRow>
          {/* Stroke color/width are meaningless on a raster image (Fabric
              draws a border around the image's bounding box, not a real
              outline of its content) — per direct user request, hidden
              entirely for isImage rather than shown-but-inert. */}
          {!isImage && (
            <>
              {/* A stroke with width 0 renders nothing (see useColorBudget's
                  own collectObjectColors — it never counts a zero-width
                  stroke as a used color for exactly this reason), so picking
                  a color for it has no visible/print effect and must not be
                  gated by the budget at all — otherwise a customer could get
                  blocked (and bounced to some other object) over a stroke
                  color that was never actually going to show up anywhere.
                  Only enforce/count the budget once the stroke is real
                  (strokeWidth > 0). */}
              {hasVisibleStroke ? (
                <ColorPicker value={obj.stroke ?? '#000000'} onChange={hex => applyColorIfAllowed(strokeColorBudget, hex, { stroke: hex })}
                  label={t('properties.strokeColor')}
                  isColorAllowed={strokeColorBudget.isColorAllowed}
                  usedColors={strokeColorBudget.usedColors}
                  isAtLimit={strokeColorBudget.isAtLimit}
                  budgetLabel={strokeColorBudget.limit != null ? `${strokeColorBudget.usedColors.length} van ${strokeColorBudget.limit} kleuren gebruikt` : undefined} />
              ) : (
                <ColorPicker value={obj.stroke ?? '#000000'} onChange={hex => set({ stroke: hex })}
                  label={t('properties.strokeColor')} />
              )}
              <PropertyRow label={t('properties.strokeWidth')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                  <Slider min={0} max={50} step={1} value={obj.strokeWidth ?? 0}
                    onChange={v => set({ strokeWidth: v })} />
                  <input type="number" value={obj.strokeWidth ?? 0} min={0} max={50}
                    onChange={e => set({ strokeWidth: Number(e.target.value) })}
                    className={PANEL_FIELD_CLASS} style={{ ...propertyInputStyle, width: 48 }} />
                </div>
              </PropertyRow>
            </>
          )}
        </ToolSection>

        {/* Transform — moved to the bottom (per direct user request, matching
            TextTool.tsx's own Add-panel placement): rotation is the
            least-used control for an already-placed object compared to
            content/font/color, so it shouldn't sit above them. Flip H/V
            removed here too, per direct user request. */}
        <ToolSection title={t('properties.transform')}>
          <PropertyRow label={t('properties.rotation')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
              <Slider min={-180} max={180} step={1} value={angle}
                onChange={v => set({ angle: v })} />
              <input type="number" value={angle} min={-180} max={180}
                onChange={e => set({ angle: Number(e.target.value) })}
                className={PANEL_FIELD_CLASS} style={{ ...propertyInputStyle, width: 48 }} />
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>°</span>
            </div>
          </PropertyRow>
          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            {[-90, -45, -5, 5, 45, 90].map(d => (
              <button key={d} onClick={() => nudgeAngle(d)} style={nudgeBtn} title={`${d > 0 ? '+' : ''}${d}°`}>
                {d > 0 && <RotateCw size={11} strokeWidth={2} style={{ marginRight: 2 }} />}
                {d}°
              </button>
            ))}
          </div>
        </ToolSection>
      </div>

      {correctionOpen && isImage && (
        <ImageCorrectionModal canvas={canvas} existingObject={obj} onClose={() => { setCorrectionOpen(false); forceUpdate(n => n + 1); }} />
      )}
    </div>
  );
}

// Everything text-specific in the Edit panel — Content (type switcher + live
// text-content edit), Typography (font search/select + size/diameter), and
// Style (fill color + Bold/Italic/Underline + letter spacing + align).
// Deliberately mirrors TextTool.tsx's (Add) section titles, field order, and
// control look (segmented pills, ColorPicker) exactly — per direct user
// request that Add and Edit not present the same options in visibly
// different ways. The one thing Add has that Edit doesn't need is the
// "Add text" submit button, since here every change applies immediately to
// the already-placed object via `set()`.
function TextEditSections({ obj, set, canvas, isCurved, fonts, fillColorBudget, applyColorIfAllowed, convertTextType }: {
  obj: any;
  set: (props: Record<string, any>) => void;
  canvas: FabricCanvas | null;
  isCurved: boolean;
  fonts: Font[];
  fillColorBudget: ReturnType<typeof useColorBudget>;
  applyColorIfAllowed: (budget: ReturnType<typeof useColorBudget>, hex: string, props: Record<string, any>) => void;
  convertTextType: (target: TextTypeKind) => void;
}) {
  const { t } = useTranslation();
  const kind = kindOf(obj);

  const handleFontChange = async (label: string) => {
    const fontRecord = fonts.find(f => f.fontLabel === label);
    if (fontRecord?.fontFile) await ensureFontLoaded(label, `/storage/${fontRecord.fontFile}`);
    set({ fontFamily: label });
  };

  // Displayed text content: vertical text stores itself pre-split with one
  // character per line (see TextTool.tsx's addText) — the editable field
  // should show the real, un-stacked string, and re-stack on every change so
  // the canvas object keeps rendering correctly as the customer types.
  const displayText = obj.isVerticalText ? String(obj.text ?? '').replace(/\n/g, '') : (obj.text ?? '');
  const handleContentChange = (value: string) => {
    set({ text: obj.isVerticalText ? value.split('').join('\n') : value });
  };

  return (
    <>
      <ToolSection title={t('text.sectionContent')}>
        <div style={segmentedGroup}>
          {(['text', 'curved', 'vertical'] as const).map(type => (
            <button key={type} onClick={() => convertTextType(type)} style={segmentBtn(kind === type)}>
              {type === 'text' ? t('text.straight') : type === 'curved' ? t('text.curved') : t('text.vertical')}
            </button>
          ))}
        </div>

        <textarea value={displayText} onChange={e => handleContentChange(e.target.value)}
          className={PANEL_FIELD_CLASS}
          style={{ ...propertyInputStyle, resize: 'vertical', minHeight: 48, fontFamily: obj.fontFamily, fontSize: 14 }} />
      </ToolSection>

      <ToolSection title={t('text.sectionTypography')}>
        <FontCombobox fonts={fonts} value={obj.fontFamily ?? ''} onChange={handleFontChange} />

        <PropertyRow label={t('text.size')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
            <Slider min={6} max={200} step={1} value={obj.fontSize ?? 20}
              onChange={v => set({ fontSize: v })} />
            <input type="number" value={obj.fontSize ?? 20} min={6} max={200}
              onChange={e => set({ fontSize: Number(e.target.value) })}
              className={PANEL_FIELD_CLASS} style={{ ...propertyInputStyle, width: 48 }} />
          </div>
        </PropertyRow>

        {isCurved && (
          <PropertyRow label={t('text.diameter')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
              <Slider min={50} max={1000} step={10} value={obj.diameter ?? 250}
                onChange={v => set({ diameter: v })} />
              <input type="number" value={obj.diameter ?? 250} min={50} max={1000}
                onChange={e => set({ diameter: Number(e.target.value) })}
                className={PANEL_FIELD_CLASS} style={{ ...propertyInputStyle, width: 48 }} />
            </div>
          </PropertyRow>
        )}
      </ToolSection>

      <ToolSection title={t('text.sectionStyle')}>
        <ColorPicker value={obj.fill ?? '#000000'} onChange={hex => applyColorIfAllowed(fillColorBudget, hex, { fill: hex })}
          isColorAllowed={fillColorBudget.isColorAllowed}
          usedColors={fillColorBudget.usedColors}
          isAtLimit={fillColorBudget.isAtLimit}
          budgetLabel={fillColorBudget.limit != null ? `${fillColorBudget.usedColors.length} van ${fillColorBudget.limit} kleuren gebruikt` : undefined} />

        <div style={segmentedGroup}>
          <button onClick={() => set({ fontWeight: obj.fontWeight === 'bold' ? 'normal' : 'bold' })}
            title={t('properties.bold')} style={{ ...iconSegmentBtn(obj.fontWeight === 'bold'), flex: 1 }}>
            <Bold size={14} strokeWidth={1.75} />
          </button>
          <button onClick={() => set({ fontStyle: obj.fontStyle === 'italic' ? '' : 'italic' })}
            title={t('properties.italic')} style={{ ...iconSegmentBtn(obj.fontStyle === 'italic'), flex: 1 }}>
            <Italic size={14} strokeWidth={1.75} />
          </button>
          <button onClick={() => set({ underline: !obj.underline })}
            title={t('properties.underline')} style={{ ...iconSegmentBtn(obj.underline === true), flex: 1 }}>
            <Underline size={14} strokeWidth={1.75} />
          </button>
        </div>

        <PropertyRow label={t('properties.letterSpacing')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
            <Slider min={-200} max={1000} step={10} value={obj.charSpacing ?? 0} onChange={v => set({ charSpacing: v })} />
            <input type="number" value={obj.charSpacing ?? 0} onChange={e => set({ charSpacing: Number(e.target.value) })}
              min={-200} max={1000} step={10} className={PANEL_FIELD_CLASS} style={{ ...propertyInputStyle, width: 48 }} />
          </div>
        </PropertyRow>

        {/* CurvedText has no line-based layout for textAlign to apply to. */}
        {!isCurved && (
          <div style={segmentedGroup}>
            {([
              ['left', AlignLeft], ['center', AlignCenter],
              ['right', AlignRight], ['justify', AlignJustify],
            ] as const).map(([a, Icon]) => (
              <button
                key={a}
                onClick={() => set({ textAlign: a })}
                title={t(`properties.align${a.charAt(0).toUpperCase()}${a.slice(1)}`)}
                style={{ ...iconSegmentBtn((obj.textAlign ?? 'left') === a), flex: 1 }}
              >
                <Icon size={14} strokeWidth={1.75} />
              </button>
            ))}
          </div>
        )}
      </ToolSection>
    </>
  );
}

// Fabric's own `type` strings are all lowercase ('textbox', 'curved-text', ...) —
// this heading is the only place that string is shown directly to the user.
function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}


function IconBtn({ children, onClick, title, danger }: { children: React.ReactNode; onClick: () => void; title: string; danger?: boolean }) {
  return (
    <button onClick={onClick} title={title} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: 26, height: 26, border: 'none', borderRadius: 5, cursor: 'pointer',
      background: 'transparent', color: danger ? '#ef4444' : 'var(--text)',
    }}>
      {children}
    </button>
  );
}

const nudgeBtn: React.CSSProperties = {
  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '4px 0', fontSize: 11, cursor: 'pointer',
  border: '1px solid var(--border)', borderRadius: 4, background: 'white', color: 'var(--text)',
};
